import React, { useState } from 'react'
import { useGameStore } from '../store/gameStore'
import { battleAPI } from '../services/api'
import './BattleArena.css'

function BattleArena() {
  const { battleState, setBattleState, endBattle, setCurrentPage, user } = useGameStore()
  const [selectedPlayerAction, setSelectedPlayerAction] = useState<number | null>(null)
  const [selectedPlayerDefense, setSelectedPlayerDefense] = useState<number | null>(null)
  const [waitingForOpponent, setWaitingForOpponent] = useState(false)
  const [battleLog, setBattleLog] = useState<any[]>([])

  if (!battleState) {
    return (
      <div className="battle-arena">
        <div className="empty-battle">
          <p>No active battle</p>
          <button onClick={() => setCurrentPage('game-hub')}>Back to Game Hub</button>
        </div>
      </div>
    )
  }

  const handleExecuteRound = async () => {
    if (selectedPlayerAction === null || selectedPlayerDefense === null) {
      alert('Please select action and defense cards')
      return
    }

    setWaitingForOpponent(true)

    try {
      const response = await battleAPI.executeRound(
        battleState,
        selectedPlayerAction,
        selectedPlayerDefense,
        Math.floor(Math.random() * battleState.opponentCards.length),
        Math.floor(Math.random() * battleState.playerCards.length)
      )

      setBattleState(response.data.battleState)
      setBattleLog([...battleLog, ...response.data.battleState.battleLog])
      setSelectedPlayerAction(null)
      setSelectedPlayerDefense(null)

      if (response.data.finished) {
        await handleBattleEnd(response.data.winner, response.data.rewards, response.data.battleState)
      }
    } catch (error) {
      alert('Error executing round')
    } finally {
      setWaitingForOpponent(false)
    }
  }

  const handleBattleEnd = async (winner: string, rewards: any, finalState: any) => {
    try {
      await battleAPI.finishBattle(null, winner, battleLog, rewards)

      alert(`${winner === 'player' ? 'Victory!' : 'Defeat!'} You earned ${rewards.coins} coins!`)
      endBattle()
      setCurrentPage('game-hub')
    } catch (error) {
      console.error('Error saving battle:', error)
    }
  }

  const playerCards = battleState.playerCards || []
  const opponentCards = battleState.opponentCards || []

  return (
    <div className="battle-arena">
      <div className="battle-header">
        <div className="battle-info">
          <div className="player-side">
            <h3>You</h3>
            <p className="remaining-cards">{playerCards.length} Cards</p>
          </div>
          <div className="round-counter">
            <span>Round {battleState.round + 1}</span>
          </div>
          <div className="opponent-side">
            <h3>Opponent</h3>
            <p className="remaining-cards">{opponentCards.length} Cards</p>
          </div>
        </div>
      </div>

      <div className="battle-field">
        <div className="opponent-field">
          <h4>Opponent's Cards</h4>
          <div className="cards-row">
            {opponentCards.map((card, idx) => (
              <div key={`${card.id}-${idx}`} className="battle-card opponent-card">
                <div className="card-name">{card.name}</div>
                <div className="card-stats">
                  <span className="hp">HP: {card.currentHp}/{card.base_hp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="battle-log">
          <h4>Battle Log</h4>
          <div className="log-content">
            {battleLog.map((entry, idx) => (
              <div key={idx} className="log-entry">
                <span className="action">{entry.action}</span>
                <span className="attacker">{entry.attacker}</span>
                <span className="vs">vs</span>
                <span className="defender">{entry.defender}</span>
                <span className="damage">-{entry.damage}HP</span>
              </div>
            ))}
          </div>
        </div>

        <div className="player-field">
          <h4>Your Cards</h4>
          <div className="cards-row">
            {playerCards.map((card, idx) => (
              <div
                key={`${card.id}-${idx}`}
                className={`battle-card player-card ${selectedPlayerAction === idx ? 'selected-attack' : ''} ${selectedPlayerDefense === idx ? 'selected-defense' : ''}`}
              >
                <div className="card-name">{card.name}</div>
                <div className="card-stats">
                  <span className="hp">HP: {card.currentHp}/{card.base_hp}</span>
                </div>
                <div className="action-buttons">
                  <button
                    className="btn-attack"
                    onClick={() => setSelectedPlayerAction(idx)}
                    disabled={waitingForOpponent}
                  >
                    Attack
                  </button>
                  <button
                    className="btn-defend"
                    onClick={() => setSelectedPlayerDefense(idx)}
                    disabled={waitingForOpponent}
                  >
                    Defend
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="battle-actions">
        <div className="selection-info">
          <p>
            Attack: {selectedPlayerAction !== null ? playerCards[selectedPlayerAction]?.name : 'Not selected'}
          </p>
          <p>
            Defense: {selectedPlayerDefense !== null ? playerCards[selectedPlayerDefense]?.name : 'Not selected'}
          </p>
        </div>
        <button
          className="btn-execute-round"
          onClick={handleExecuteRound}
          disabled={selectedPlayerAction === null || selectedPlayerDefense === null || waitingForOpponent}
        >
          {waitingForOpponent ? 'Processing...' : 'Execute Round'}
        </button>
      </div>
    </div>
  )
}

export default BattleArena
